import React from 'react';
import { Button, Dropdown, DropdownItem, DropdownMenu, DropdownTrigger } from '@heroui/react';
import { RiArrowDownSLine, RiSearchLine } from '@remixicon/react';

export interface SearchDropdownProps {
  widthPx?: number;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  children: React.ReactNode; // the actual search form controls
  onApply?: () => void;
  onClear?: () => void;
}

export function SearchDropdown(props: SearchDropdownProps) {
  const { widthPx = 140, isOpen, setIsOpen, children, onApply, onClear } = props;

  // Handle Enter key press
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && onApply) {
      e.preventDefault();
      onApply();
    }
  };

  // Clone children to add onKeyDown to all Input components
  const childrenWithKeyDown = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      // Check if it's an Input component by checking the component name or displayName
      const componentName = (child.type as any)?.displayName || (child.type as any)?.name;
      const isInput =
        componentName === 'Input' ||
        (child.props &&
          typeof child.props === 'object' &&
          ('label' in child.props || 'placeholder' in child.props || 'value' in child.props));

      if (isInput) {
        return React.cloneElement(child, {
          ...child.props,
          onKeyDown: (e: React.KeyboardEvent) => {
            // Call original onKeyDown if it exists
            if (child.props.onKeyDown) {
              child.props.onKeyDown(e);
            }
            // Call our handler
            handleKeyDown(e);
          }
        });
      }
    }

    return child;
  });

  return (
    <Dropdown isOpen={isOpen} placement="bottom-start" onOpenChange={setIsOpen}>
      <DropdownTrigger>
        <Button className="h-12" endContent={<RiArrowDownSLine className="h-4 w-4" />} variant="flat">
          <RiSearchLine className="h-4 w-4" />
          <span>Search</span>
        </Button>
      </DropdownTrigger>
      <DropdownMenu aria-label="Advanced search" closeOnSelect={false}>
        <DropdownItem
          key="search-form"
          isReadOnly
          className="!bg-transparent data-[hover=true]:bg-transparent data-[selectable=true]:focus:bg-transparent"
        >
          <div
            className="flex flex-col gap-2"
            style={{ width: `${widthPx}px` }}
            onKeyDown={handleKeyDown}
            role="form"
            tabIndex={0}
          >
            {childrenWithKeyDown}
            <div className="flex justify-end gap-2 pt-1">
              {onClear && (
                <Button size="sm" variant="light" onPress={onClear}>
                  Clear
                </Button>
              )}
              {onApply && (
                <Button color="primary" size="sm" onPress={onApply}>
                  Apply
                </Button>
              )}
            </div>
          </div>
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  );
}

export default SearchDropdown;
